import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Contains a render error to one section instead of the whole page.
 *
 * Without this, any exception thrown while rendering unmounts the entire
 * React tree and leaves the bare page background -- which on this theme is
 * near-black, so the symptom is "the screen went black" with no clue as to
 * which component failed. A dashboard of seventeen charts should lose the
 * one chart that broke, not all of them.
 */
interface Props {
  children: ReactNode;
  /** Shown in place of the failed subtree. */
  label?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Keep the detail reachable in the console; the UI shows only a summary.
    console.error(`[${this.props.label ?? "component"}] render failed`, error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          border: "1px solid var(--status-critical)",
          color: "var(--status-critical)",
          padding: "10px 12px",
          fontSize: 12,
          letterSpacing: "0.06em",
        }}
      >
        <div>!! {this.props.label ?? "SECTION"} FAILED TO RENDER</div>
        <div style={{ color: "var(--text-muted)", marginTop: 4 }}>{error.message}</div>
      </div>
    );
  }
}
